import { useState, useRef, useEffect, useCallback } from 'react';
import {
  loadPoseLandmarker,
  detectPoseFrame,
  drawPoseSkeleton,
  poseSmoother,
  calculateJointAngles,
  phaseAnalyzer,
} from '../api/poseApi';

const DEFAULT_ANGLES = {
  drawElbowAngle: 0,
  bowArmAngle: 0,
  torsoAngle: 90,
  angleSupportArm: 0,
  angleDrawArm: 0,
  angle16_12_24: 0,
  angle13_11_23: 0,
};

const DEFAULT_PHASE = {
  phase: 'SETUP',
  anchorDuration: 0,
  stabilityScore: 100,
};

export function usePoseDetection({ videoRef, isStreaming, isPaused = false, dominantHand = 'right' }) {
  const canvasRef = useRef(null);
  const isStreamingRef = useRef(isStreaming);
  const isPausedRef = useRef(isPaused);
  const [isLoadingModel, setIsLoadingModel] = useState(true);
  const [modelError, setModelError] = useState(null);
  const [fps, setFps] = useState(0);
  const [hasPoseDetected, setHasPoseDetected] = useState(false);
  const [angles, setAngles] = useState(DEFAULT_ANGLES);
  const [phaseData, setPhaseData] = useState(DEFAULT_PHASE);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const clearCanvas = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
      // 강제 버퍼 리셋으로 캔버스 잔상 완벽 제거
      canvas.width = canvas.width;
    }
  }, []);

  useEffect(() => {
    let animId = null;
    let landmarker = null;
    let frameCount = 0;
    let fpsTimer = performance.now();

    async function init() {
      try {
        setIsLoadingModel(true);
        landmarker = await loadPoseLandmarker();
        setIsLoadingModel(false);
      } catch (err) {
        setModelError('AI 포즈 모델 로드에 실패했습니다.');
        setIsLoadingModel(false);
      }
    }
    init();

    function renderLoop(time) {
      // 스트리밍이 중지되었거나 컴포넌트가 비활성화된 경우 즉시 루프 중단
      if (!isStreamingRef.current || !videoRef.current || !canvasRef.current || !landmarker) {
        return;
      }

      // 일시 정지 상태가 아닐 때만 새 프레임 추론 및 캔버스 갱신
      if (!isPausedRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        if (video.videoWidth > 0 && video.videoHeight > 0) {
          if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          const { landmarks } = detectPoseFrame(landmarker, video, time);
          const detected = Boolean(landmarks && landmarks.length > 0);
          setHasPoseDetected(detected);

          if (detected) {
            const smoothed = poseSmoother.smooth(landmarks[0], time);
            drawPoseSkeleton(ctx, smoothed, canvas.width, canvas.height, dominantHand);

            // 각도 및 사격 페이즈 계산
            const calculatedAngles = calculateJointAngles(smoothed, canvas.width, canvas.height, dominantHand);
            const analyzedPhase = phaseAnalyzer.analyze(smoothed, time, calculatedAngles);
            setAngles(calculatedAngles);
            setPhaseData(analyzedPhase);
          } else {
            poseSmoother.reset();
            phaseAnalyzer.reset();
          }
        }

        frameCount++;
        if (time - fpsTimer >= 1000) {
          setFps(Math.round((frameCount * 1000) / (time - fpsTimer)));
          frameCount = 0;
          fpsTimer = time;
        }
      }

      animId = requestAnimationFrame(renderLoop);
    }

    if (isStreaming) {
      animId = requestAnimationFrame(renderLoop);
    } else {
      if (animId) {
        cancelAnimationFrame(animId);
        animId = null;
      }
      clearCanvas();
      setHasPoseDetected(false);
      setFps(0);
      setAngles(DEFAULT_ANGLES);
      setPhaseData(DEFAULT_PHASE);
      poseSmoother.reset();
      phaseAnalyzer.reset();
    }

    return () => {
      if (animId) {
        cancelAnimationFrame(animId);
        animId = null;
      }
      clearCanvas();
      setHasPoseDetected(false);
      setFps(0);
      setAngles(DEFAULT_ANGLES);
      setPhaseData(DEFAULT_PHASE);
      poseSmoother.reset();
      phaseAnalyzer.reset();
    };
  }, [videoRef, isStreaming, dominantHand, clearCanvas]);

  return {
    canvasRef,
    isLoadingModel,
    modelError,
    fps,
    hasPoseDetected,
    angles,
    phaseData,
    clearCanvas,
  };
}
